/*
  MIMOYA — ebook offert et protection d'expédition ajoutés avec la crème, en une seule requête.
  Fichier ajouté hors référentiel FullStack : survit aux mises à jour (le crochet « MIMOYA » de
  assets/product-form.js, lui, est à réappliquer).

  Appelé par product-form.js juste avant l'ajout au panier : renvoie les lignes à ajouter en plus.
  Avant : le tiroir s'ouvrait vide le temps que le moteur de cadeaux (auto-gift-processor.js) relise le
  panier et ajoute l'ebook, puis que la protection s'ajoute à son tour (4 appels au serveur après
  l'ajout, ~2 s). Le moteur de cadeaux reste actif derrière et corrige tout écart éventuel.

  Mêmes règles que lui :
  - cadeau ajouté quand le nombre de flacons de sa collection atteint le seuil, s'il n'est pas déjà
    dans le panier (ni déjà envoyé avec un ajout en cours : jamais deux fois le même cadeau). Pas de
    mémoire de refus : le cadeau suit la quantité, comme le prix ;
  - cadeaux à choix de variante (fenêtre de choix) et seuils en montant : laissés au moteur ;
  - moteur coupé (Paramètres du thème) : aucun élément <auto-gift> dans la page, aucun cadeau ajouté ;
  - protection ajoutée si « activée par défaut », disponible, absente du panier et non décochée pendant
    la session (même mémoire que assets/mimoya-shipping-protection.js).

  Retrait ou baisse de quantité dans le tiroir (getLineUpdates, appelé par assets/cart-drawer.js) :
  renvoie en plus, pour la même requête /cart/update.js, les cadeaux qui ne sont plus débloqués et,
  si plus aucun produit payant ne reste, tout le reste (cadeaux, protection). Avant : le tiroir passait
  par 2 à 3 états (cadeau encore là, puis protection seule) pendant 2 à 5 s, le moteur de cadeaux et
  la protection se retirant chacun à leur tour après coup.
*/
import { buildGiftLineProperties } from '@theme/auto-gift-processor';

function readJson(element) {
  if (!element) return null;
  try {
    return JSON.parse(element.textContent);
  } catch (error) {
    return null;
  }
}

function cartState() {
  return readJson(document.querySelector('cart-drawer [data-mimoya-cart-state]'));
}

function productInfo(form) {
  const productId = form?.dataset?.productId;
  if (!productId) return null;
  return readJson(document.querySelector(`script[data-mimoya-product-info="${productId}"]`));
}

// MIMOYA : cadeaux déjà envoyés avec un ajout dont la réponse n'est pas encore affichée (double clic,
// deux ajouts rapprochés) : on ne les renvoie pas, sinon Shopify les additionne (cadeau × 2).
// La mémoire tombe dès que le tiroir affiche un nouvel état du panier (ou au bout de 15 s).
let pending = null;
const PENDING_MS = 15000;

function stateSignature() {
  return document.querySelector('cart-drawer [data-mimoya-cart-state]')?.textContent || '';
}

function pendingBlocks() {
  if (pending && (pending.signature !== stateSignature() || Date.now() - pending.time > PENDING_MS)) pending = null;
  return pending ? pending.blocks : new Set();
}

function rememberPending(blockIds) {
  if (blockIds.length === 0) return;
  const blocks = new Set(pendingBlocks());
  blockIds.forEach((id) => blocks.add(id));
  pending = { signature: stateSignature(), time: Date.now(), blocks };
}

// Cadeaux « nombre de tubes » débloqués et absents du panier. stateItems : lignes du panier
// ({ quantity, gift, collections }) ; extraEligible(inCollection) : tubes ajoutés en plus.
// Pas de mémoire de refus : un cadeau suit toujours la quantité (comme le prix).
function unlockedGiftLines(stateItems, extraEligible) {
  const lines = [];
  const blocks = [];
  const skip = pendingBlocks();

  document.querySelectorAll('auto-gift').forEach((gift) => {
    const data = gift.dataset;
    if (data.triggerType !== 'quantity' || data.needsVariantChoice === 'true') return;
    if (skip.has(data.blockId)) return;
    if (stateItems.some((item) => item.gift === data.blockId && Number(item.quantity) > 0)) return;

    const config = readJson(gift.querySelector('[data-ref="gift-config"]'));
    if (!config || !Array.isArray(config.products) || config.products.length === 0) return;

    const handle = data.collectionHandle || '';
    const inCollection = (collections) => !handle || (collections || []).includes(handle);

    let quantity = extraEligible(inCollection);
    stateItems.forEach((item) => {
      if (!item.gift && inCollection(item.collections)) quantity += Number(item.quantity) || 0;
    });
    if (quantity < parseFloat(data.threshold)) return;

    const properties = buildGiftLineProperties(data.blockId, config.giftBadge);
    const quantityPerProduct = parseInt(data.quantityPerProduct, 10) || 1;
    config.products.forEach((product) => {
      if (product.defaultVariantId) {
        lines.push({ id: product.defaultVariantId, quantity: quantityPerProduct, properties });
      }
    });
    blocks.push(data.blockId);
  });

  return { lines, blocks };
}

function giftLines(items, form, state) {
  const info = productInfo(form);
  if (!info) return [];

  const productVariants = new Set((info.variants || []).map(Number));
  const { lines, blocks } = unlockedGiftLines(state.items, (inCollection) => {
    if (!inCollection(info.collections)) return 0;
    return items.reduce((total, item) => total + (productVariants.has(Number(item.id)) ? Number(item.quantity) || 0 : 0), 0);
  });
  rememberPending(blocks);
  return lines;
}

// Quantité augmentée dans le tiroir : cadeaux nouvellement débloqués, ajoutés par assets/cart-drawer.js
// juste après la mise à jour et avant l'affichage (prix et cadeaux changent en même temps).
function lineAdds(line, quantity) {
  const state = cartState();
  if (!state || !Array.isArray(state.items)) return [];

  const { index, target } = findLine(state.items, line);
  if (!target || target.gift) return [];

  const newQuantity = Math.max(0, parseInt(quantity, 10) || 0);
  if (newQuantity <= (Number(target.quantity) || 0)) return [];

  const next = state.items.map((item, i) => ({
    ...item,
    quantity: i === index ? newQuantity : Number(item.quantity) || 0,
  }));
  const { lines, blocks } = unlockedGiftLines(next, () => 0);
  rememberPending(blocks);
  return lines;
}

function protectionLines(state) {
  const protection = document.querySelector('cart-drawer mimoya-shipping-protection');
  if (!protection) return [];

  const data = protection.dataset;
  if (data.defaultOn !== 'true' || data.available === 'false') return [];

  const variantId = Number(data.variantId);
  if (!variantId || state.items.some((item) => Number(item.variant_id) === variantId)) return [];

  try {
    if (sessionStorage.getItem('mimoya-protection-off') === '1') return [];
  } catch (error) {
    // Stockage indisponible : la protection suit simplement le réglage par défaut.
  }

  return [{ id: variantId, quantity: 1 }];
}

// Ligne visée : par sa clé Shopify (stable) ; le numéro de ligne change dès qu'un cadeau est ajouté
// (Shopify place la nouvelle ligne en tête) et un clic rapide visait alors la mauvaise ligne :
// « + » sur la crème augmentait un cadeau ou la protection, « − » ne retirait pas les cadeaux.
function findLine(items, ref) {
  const key = typeof ref === 'string' && ref.includes(':') ? ref : null;
  const index = key ? items.findIndex((item) => item.key === key) : parseInt(ref, 10) - 1;
  return { index, target: index >= 0 ? items[index] : null };
}

function lineUpdates(line, quantity) {
  const state = cartState();
  if (!state || !Array.isArray(state.items)) return null;

  const { index, target } = findLine(state.items, line);
  if (!target || !target.key) return null;

  const newQuantity = Math.max(0, parseInt(quantity, 10) || 0);
  const next = state.items.map((item, i) => ({
    ...item,
    quantity: i === index ? newQuantity : Number(item.quantity) || 0,
  }));

  const updates = {};
  const drop = (item) => {
    if (item.quantity > 0 && item.key) {
      updates[item.key] = 0;
      item.quantity = 0;
    }
  };

  // Cadeaux au nombre de flacons dont le seuil n'est plus atteint (pas quand on retire le cadeau
  // lui-même : c'est un refus, géré par le moteur de cadeaux).
  if (!target.gift) {
    document.querySelectorAll('auto-gift').forEach((gift) => {
      const data = gift.dataset;
      if (data.triggerType !== 'quantity') return;

      const handle = data.collectionHandle || '';
      const inCollection = (collections) => !handle || (collections || []).includes(handle);

      let eligible = 0;
      next.forEach((item) => {
        if (!item.gift && inCollection(item.collections)) eligible += item.quantity;
      });
      if (eligible >= parseFloat(data.threshold)) return;

      next.forEach((item) => {
        if (item.gift === data.blockId) drop(item);
      });
    });
  }

  // Plus aucun produit payant : le panier se vide d'un coup (cadeaux, protection d'expédition).
  const protectionId = Number(document.querySelector('cart-drawer mimoya-shipping-protection')?.dataset.variantId) || null;
  const paidLeft = next.some((item) => item.quantity > 0 && !item.gift && Number(item.variant_id) !== protectionId);
  if (!paidLeft) next.forEach(drop);

  if (Object.keys(updates).length === 0) return null;
  if (!(target.key in updates)) updates[target.key] = newQuantity;
  return { updates };
}

window.mimoyaCartExtras = {
  getExtras(items, form) {
    const state = cartState();
    if (!state || !Array.isArray(state.items) || !Array.isArray(items) || items.length === 0) return [];
    return giftLines(items, form, state).concat(protectionLines(state));
  },

  // line : clé de la ligne (ou numéro de ligne Shopify, 1 = première), quantity : nouvelle quantité (0 = retrait).
  getLineUpdates(line, quantity) {
    return lineUpdates(line, quantity);
  },

  // Cadeaux à ajouter quand la quantité d'une ligne augmente (même numérotation).
  getLineAdds(line, quantity) {
    return lineAdds(line, quantity);
  },
};
